
import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import type { GA4Data } from "@/lib/types";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

function fmt(n: number) {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return n.toLocaleString("cs-CZ");
}

interface GeoTableProps {
  data: GA4Data[];
}

interface GeoRow {
  name: string;
  sessions: number;
  users: number;
  newUsers: number;
  bounceRate: number;
  engRate: number;
  count: number;
}

function aggregate(data: GA4Data[], key: "city" | "country" | "region"): GeoRow[] {
  const map = new Map<string, GeoRow>();
  data.forEach((d) => {
    const name = d[key];
    if (!name) return;
    const existing = map.get(name) || { name, sessions: 0, users: 0, newUsers: 0, bounceRate: 0, engRate: 0, count: 0 };
    existing.sessions += d.sessions;
    existing.users += d.totalusers;
    existing.newUsers += d.newusers;
    existing.bounceRate += d.bounce_rate;
    existing.engRate += d.engagement_rate;
    existing.count++;
    map.set(name, existing);
  });
  return Array.from(map.values())
    .map((r) => ({ ...r, bounceRate: r.count > 0 ? r.bounceRate / r.count : 0, engRate: r.count > 0 ? r.engRate / r.count : 0 }))
    .sort((a, b) => b.sessions - a.sessions);
}

function GeoRowsTable({ rows, maxRows = 15 }: { rows: GeoRow[]; maxRows?: number }) {
  const maxSessions = rows.length > 0 ? rows[0].sessions : 1;

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Lokalita</TableHead>
          <TableHead className="text-right">Sessions</TableHead>
          <TableHead className="text-right">Uživatelé</TableHead>
          <TableHead className="text-right">Noví</TableHead>
          <TableHead className="text-right">Eng. rate</TableHead>
          <TableHead className="w-[120px]"></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.slice(0, maxRows).map((row) => (
          <TableRow key={row.name}>
            <TableCell className="font-medium">{row.name}</TableCell>
            <TableCell className="text-right">{fmt(row.sessions)}</TableCell>
            <TableCell className="text-right">{fmt(row.users)}</TableCell>
            <TableCell className="text-right">{fmt(row.newUsers)}</TableCell>
            <TableCell className="text-right">{(row.engRate * 100).toFixed(1)}%</TableCell>
            <TableCell>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-[var(--primary)] h-2 rounded-full"
                  style={{ width: `${(row.sessions / maxSessions) * 100}%` }}
                />
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export function GeoOverview({ data }: GeoTableProps) {
  const cities = useMemo(() => aggregate(data, "city"), [data]);
  const countries = useMemo(() => aggregate(data, "country"), [data]);
  const regions = useMemo(() => aggregate(data, "region"), [data]);

  const hasCities = cities.length > 0;
  const hasCountries = countries.length > 0;
  const hasRegions = regions.length > 0;

  if (!hasCities && !hasCountries && !hasRegions) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Lokality</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-4">
            Geo data nejsou k dispozici. Aktualizujte Windsor.io GA4 connector o pole country, city, region.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Lokality návštěvníků</CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue={hasCities ? "cities" : hasCountries ? "countries" : "regions"}>
          <TabsList>
            {hasCities && <TabsTrigger value="cities">Města ({cities.length})</TabsTrigger>}
            {hasRegions && <TabsTrigger value="regions">Regiony ({regions.length})</TabsTrigger>}
            {hasCountries && <TabsTrigger value="countries">Země ({countries.length})</TabsTrigger>}
          </TabsList>
          {hasCities && (
            <TabsContent value="cities" className="mt-3">
              <GeoRowsTable rows={cities} />
            </TabsContent>
          )}
          {hasRegions && (
            <TabsContent value="regions" className="mt-3">
              <GeoRowsTable rows={regions} />
            </TabsContent>
          )}
          {hasCountries && (
            <TabsContent value="countries" className="mt-3">
              <GeoRowsTable rows={countries} />
            </TabsContent>
          )}
        </Tabs>
      </CardContent>
    </Card>
  );
}

export function DemoOverview({ data }: GeoTableProps) {
  const ages = useMemo(() => {
    const map = new Map<string, number>();
    data.forEach((d) => { if (d.age) map.set(d.age, (map.get(d.age) || 0) + d.sessions); });
    return Array.from(map.entries()).map(([name, value]) => ({ name, value })).sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  const genders = useMemo(() => {
    const map = new Map<string, number>();
    data.forEach((d) => { if (d.gender) map.set(d.gender, (map.get(d.gender) || 0) + d.sessions); });
    return Array.from(map.entries()).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [data]);

  if (ages.length === 0 && genders.length === 0) return null;

  const maxAge = ages.length > 0 ? Math.max(...ages.map((a) => a.value)) : 1;
  const maxGender = genders.length > 0 ? Math.max(...genders.map((g) => g.value)) : 1;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Demografie návštěvníků</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {ages.length > 0 && (
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-3">Věk</p>
              <div className="space-y-2">
                {ages.map((a) => (
                  <div key={a.name} className="flex items-center gap-3">
                    <span className="text-xs w-[60px] text-right text-muted-foreground">{a.name}</span>
                    <div className="flex-1 bg-muted rounded-full h-5 relative">
                      <div
                        className="bg-[var(--primary)] h-5 rounded-full flex items-center justify-end pr-2"
                        style={{ width: `${Math.max((a.value / maxAge) * 100, 8)}%` }}
                      >
                        <span className="text-[10px] font-semibold text-primary-foreground">{fmt(a.value)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {genders.length > 0 && (
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-3">Pohlaví</p>
              <div className="space-y-2">
                {genders.map((g) => (
                  <div key={g.name} className="flex items-center gap-3">
                    <span className="text-xs w-[60px] text-right text-muted-foreground">
                      {g.name === "male" ? "Muži" : g.name === "female" ? "Ženy" : g.name}
                    </span>
                    <div className="flex-1 bg-muted rounded-full h-5 relative">
                      <div
                        className="bg-[var(--primary)] h-5 rounded-full flex items-center justify-end pr-2"
                        style={{ width: `${Math.max((g.value / maxGender) * 100, 8)}%` }}
                      >
                        <span className="text-[10px] font-semibold text-primary-foreground">{fmt(g.value)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
